import { useEffect, useState, useCallback } from 'react';
import {
  institutionUsers,
  institutionDepartments,
  institutionRoles,
  type User,
  type Department,
  type Role,
  type CreateUserInput,
  type UpdateUserInput,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { BulkImportModal } from '@/components/staff/BulkImportModal';
import styles from './UsersPage.module.css';

type ModalMode = 'create' | 'edit' | 'roles' | 'reset-password' | null;

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<CreateUserInput>>({});
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bulk import modal
  const [showBulkImport, setShowBulkImport] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page,
        perPage: 20,
      };
      if (searchQuery) params.search = searchQuery;
      if (filterDepartment) params.departmentId = filterDepartment;
      if (filterStatus === 'active') params.isActive = true;
      if (filterStatus === 'inactive') params.isActive = false;

      const response = await institutionUsers.list(params);
      if (response.success && response.data) {
        setUsers(response.data);
        if (response.meta) {
          setTotalPages(response.meta.totalPages);
          setTotal(response.meta.total);
        }
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, filterDepartment, filterStatus]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [deptRes, rolesRes] = await Promise.all([
        institutionDepartments.list(),
        institutionRoles.list(),
      ]);
      if (deptRes.success && deptRes.data) {
        setDepartments(deptRes.data);
      }
      if (rolesRes.success && rolesRes.data) {
        setRoles(rolesRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  function openCreateModal() {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      employeeId: '',
      jobTitle: '',
      phone: '',
      departmentId: '',
      securityClearance: 1,
      roleIds: [],
    });
    setSelectedRoleIds([]);
    setError(null);
    setModalMode('create');
  }

  function openEditModal(user: User) {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      employeeId: user.employeeId || '',
      jobTitle: user.jobTitle || '',
      phone: user.phone || '',
      departmentId: user.department?.id || '',
      securityClearance: user.securityClearance,
    });
    setError(null);
    setModalMode('edit');
  }

  function openRolesModal(user: User) {
    setSelectedUser(user);
    setSelectedRoleIds(user.roles.map((r) => r.id));
    setError(null);
    setModalMode('roles');
  }

  function openResetPasswordModal(user: User) {
    setSelectedUser(user);
    setNewPassword('');
    setError(null);
    setModalMode('reset-password');
  }

  function closeModal() {
    setModalMode(null);
    setSelectedUser(null);
    setFormData({});
    setSelectedRoleIds([]);
    setNewPassword('');
    setError(null);
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const input: CreateUserInput = {
        email: formData.email!,
        password: formData.password!,
        firstName: formData.firstName!,
        lastName: formData.lastName!,
        employeeId: formData.employeeId || undefined,
        jobTitle: formData.jobTitle || undefined,
        phone: formData.phone || undefined,
        departmentId: formData.departmentId || undefined,
        securityClearance: formData.securityClearance || 1,
        roleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
      };

      const response = await institutionUsers.create(input);
      if (response.success) {
        closeModal();
        fetchUsers();
      } else {
        setError(response.error?.message || 'Failed to create user');
      }
    } catch (err) {
      setError('An error occurred while creating the user');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateUser(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser) return;

    setSubmitting(true);
    setError(null);

    try {
      const input: UpdateUserInput = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        employeeId: formData.employeeId || undefined,
        jobTitle: formData.jobTitle || undefined,
        phone: formData.phone || undefined,
        departmentId: formData.departmentId || undefined,
        securityClearance: formData.securityClearance,
      };

      const response = await institutionUsers.update(selectedUser.id, input);
      if (response.success) {
        closeModal();
        fetchUsers();
      } else {
        setError(response.error?.message || 'Failed to update user');
      }
    } catch (err) {
      setError('An error occurred while updating the user');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateRoles(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await institutionUsers.updateRoles(selectedUser.id, selectedRoleIds);
      if (response.success) {
        closeModal();
        fetchUsers();
      } else {
        setError(response.error?.message || 'Failed to update roles');
      }
    } catch (err) {
      setError('An error occurred while updating roles');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await institutionUsers.resetPassword(selectedUser.id, newPassword);
      if (response.success) {
        closeModal();
      } else {
        setError(response.error?.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('An error occurred while resetting the password');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleStatus(user: User) {
    try {
      if (user.isActive) {
        await institutionUsers.deactivate(user.id);
      } else {
        await institutionUsers.activate(user.id);
      }
      fetchUsers();
    } catch (err) {
      console.error('Failed to toggle user status:', err);
    }
  }

  function handleRoleToggle(roleId: string) {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Staff Records Management</h1>
          <p className={styles.subtitle}>Manage staff accounts, roles, and permissions</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={() => setShowBulkImport(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            Import Staff
          </Button>
          <Button onClick={openCreateModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Staff
          </Button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className={styles.searchInput}
          />
        </div>
        <select
          value={filterDepartment}
          onChange={(e) => {
            setFilterDepartment(e.target.value);
            setPage(1);
          }}
          className={styles.filterSelect}
        >
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
          className={styles.filterSelect}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading users...</div>
        ) : users.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <p>No users found</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Department</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className={styles.userCell}>
                      <div className={styles.avatar}>
                        {user.firstName[0]}
                        {user.lastName[0]}
                      </div>
                      <div className={styles.userInfo}>
                        <span className={styles.userName}>
                          {user.firstName} {user.lastName}
                        </span>
                        <span className={styles.userEmail}>{user.email}</span>
                        {user.jobTitle && (
                          <span className={styles.userTitle}>{user.jobTitle}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {user.department ? (
                      <span className={styles.departmentBadge}>{user.department.name}</span>
                    ) : (
                      <span className={styles.noDepartment}>Unassigned</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.rolesList}>
                      {user.roles.length > 0 ? (
                        user.roles.map((role) => (
                          <span key={role.id} className={styles.roleBadge}>
                            {role.name}
                          </span>
                        ))
                      ) : (
                        <span className={styles.noRoles}>No roles</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        user.isActive ? styles.statusActive : styles.statusInactive
                      }`}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => openEditModal(user)}
                        title="Edit user"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className={styles.actionBtn}
                        onClick={() => openRolesModal(user)}
                        title="Manage roles"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                      </button>
                      <button
                        className={styles.actionBtn}
                        onClick={() => openResetPasswordModal(user)}
                        title="Reset password"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </button>
                      <button
                        className={`${styles.actionBtn} ${
                          user.isActive ? styles.actionDanger : styles.actionSuccess
                        }`}
                        onClick={() => handleToggleStatus(user)}
                        title={user.isActive ? 'Deactivate user' : 'Activate user'}
                      >
                        {user.isActive ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Showing {(page - 1) * 20 + 1} - {Math.min(page * 20, total)} of {total} users
          </span>
          <div className={styles.paginationButtons}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={styles.paginationBtn}
            >
              Previous
            </button>
            <span className={styles.pageNumber}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={styles.paginationBtn}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit User Modal */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Add New User' : 'Edit User'}
      >
        <form onSubmit={modalMode === 'create' ? handleCreateUser : handleUpdateUser}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.formRow}>
            <Input
              label="First Name"
              value={formData.firstName || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
              required
            />
            <Input
              label="Last Name"
              value={formData.lastName || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
              required
            />
          </div>

          {modalMode === 'create' && (
            <>
              <Input
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
              <Input
                label="Password"
                type="password"
                value={formData.password || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                required
                hint="Must be at least 8 characters"
              />
            </>
          )}

          <div className={styles.formRow}>
            <Input
              label="Staff ID"
              value={formData.employeeId || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, employeeId: e.target.value }))}
            />
            <Input
              label="Job Title"
              value={formData.jobTitle || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, jobTitle: e.target.value }))}
            />
          </div>

          <Input
            label="Phone"
            value={formData.phone || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
          />

          <Select
            label="Department"
            value={formData.departmentId || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, departmentId: e.target.value }))}
          >
            <option value="">Select Department</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>

          <Select
            label="Security Clearance"
            value={String(formData.securityClearance || 1)}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, securityClearance: Number(e.target.value) }))
            }
          >
            <option value="1">Level 1 - Public</option>
            <option value="2">Level 2 - Internal</option>
            <option value="3">Level 3 - Confidential</option>
            <option value="4">Level 4 - Secret</option>
            <option value="5">Level 5 - Top Secret</option>
          </Select>

          {modalMode === 'create' && (
            <div className={styles.rolesSection}>
              <label className={styles.rolesLabel}>Assign Roles</label>
              <div className={styles.rolesGrid}>
                {roles.map((role) => (
                  <label key={role.id} className={styles.roleCheckbox}>
                    <input
                      type="checkbox"
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : modalMode === 'create' ? 'Create User' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Manage Roles Modal */}
      <Modal isOpen={modalMode === 'roles'} onClose={closeModal} title="Manage Roles">
        <form onSubmit={handleUpdateRoles}>
          {error && <div className={styles.error}>{error}</div>}

          <p className={styles.modalText}>
            Select roles for{' '}
            <strong>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>
          </p>

          <div className={styles.rolesGrid}>
            {roles.map((role) => (
              <label key={role.id} className={styles.roleCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedRoleIds.includes(role.id)}
                  onChange={() => handleRoleToggle(role.id)}
                />
                <div className={styles.roleInfo}>
                  <span className={styles.roleName}>{role.name}</span>
                  {role.description && (
                    <span className={styles.roleDescription}>{role.description}</span>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Update Roles'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={modalMode === 'reset-password'} onClose={closeModal} title="Reset Password">
        <form onSubmit={handleResetPassword}>
          {error && <div className={styles.error}>{error}</div>}

          <p className={styles.modalText}>
            Set a new password for{' '}
            <strong>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>
          </p>

          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            hint="Must be at least 8 characters"
          />

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || newPassword.length < 8}>
              {submitting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={() => {
          fetchUsers();
          fetchReferenceData();
        }}
      />
    </div>
  );
}
